import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dices, Tv, X, Trash2, Ticket, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useLottery(tid) {
  const [data, setData] = useState(null);
  const load = () => axios.get(`${API}/public/tournaments/${tid}/lottery`).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, [tid]); // eslint-disable-line
  return { data, setData, load };
}

export function LotteryBoard({ board, highlight, compact = false, testid = "lottery-board" }) {
  if (!board?.length) return <div className="text-xs text-zinc-500 border border-pb-border p-4 text-center" data-testid={`${testid}-empty`}>Numbers appear once results are in — 1 = most PBR points earned at this event.</div>;
  return (
    <div className={`grid gap-1.5 ${compact ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6"}`} data-testid={testid}>
      {board.map((b) => (
        <div key={b.user_id} data-testid={`${testid}-${b.number}`}
          className={`flex items-center gap-2 border px-2 py-1.5 transition-colors ${highlight === b.number ? "border-pb-red bg-pb-red/15 shadow-[0_0_14px_rgba(163,230,53,0.5)]" : "border-pb-border bg-black/40"}`}>
          <span className={`font-display font-black text-xl w-9 text-center ${highlight === b.number ? "text-pb-red" : "text-pb-cyan"}`}>{b.number}</span>
          <div className="min-w-0"><div className="text-xs font-bold text-white truncate">{b.name}</div><div className="text-[9px] text-zinc-500">{b.points} PBR pts</div></div>
        </div>
      ))}
    </div>
  );
}

function Spinner({ target, max, onDone }) {
  const [n, setN] = useState(1);
  useEffect(() => {
    let i = 0; const total = 28;
    const tick = () => {
      i += 1;
      if (i >= total) { setN(target); onDone?.(); return; }
      setN(1 + Math.floor(Math.random() * Math.max(1, max)));
      setTimeout(tick, 40 + i * i * 1.1);
    };
    tick();
  }, [target, max]); // eslint-disable-line
  return <span data-testid="lottery-spin-number">{n}</span>;
}

export default function TdLotteryPanel({ tid, tv = false, onCloseTv }) {
  const { data, load } = useLottery(tid);
  const [rangeMax, setRangeMax] = useState("");
  const [prize, setPrize] = useState("");
  const [excl, setExcl] = useState(false);
  const [spin, setSpin] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showTv, setShowTv] = useState(false);
  const ranked = data?.ranked || 0;
  const max = Number(rangeMax) || ranked || 1;
  const boxRef = useRef(null);

  useEffect(() => { if (!rangeMax && ranked) setRangeMax(String(ranked)); }, [ranked]); // eslint-disable-line

  const draw = async () => {
    if (max < 1) return toast.error("Range must be at least 1");
    setBusy(true); setResult(null);
    try {
      const { data: d } = await api.post(`/tournaments/${tid}/lottery/draw`, { range_max: max, prize, exclude_previous_winners: excl });
      setSpin(d.draw);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const landed = () => {
    setResult(spin); setSpin(null); setBusy(false); load();
    if (spin?.winner) toast.success(`#${spin.number} — ${spin.winner.name}!`); else toast(`#${spin?.number} — no player on that number. Roll again!`);
  };
  const remove = async (id) => { await api.delete(`/tournaments/${tid}/lottery/draws/${id}`).catch(() => {}); load(); };

  if (tv || showTv) {
    const inner = (
      <div className="fixed inset-0 z-[140] bg-black text-white flex flex-col p-6 sm:p-10" data-testid="lottery-tv">
        <div className="flex items-center justify-between">
          <div><div className="text-[11px] uppercase tracking-[0.4em] text-pb-cyan font-bold">PBR Lottery · {data?.tournament}</div><div className="font-display font-black uppercase text-3xl sm:text-5xl">Numbers 1 – {max}</div></div>
          <button onClick={() => { setShowTv(false); onCloseTv?.(); }} className="text-zinc-500 hover:text-white" data-testid="lottery-tv-close"><X className="w-8 h-8" /></button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-6">
          <div className={`font-display font-black tabular-nums leading-none text-[26vw] sm:text-[18vw] ${result?.winner ? "text-pb-red" : "text-white"}`} data-testid="lottery-tv-number">
            {spin ? <Spinner target={spin.number} max={max} onDone={landed} /> : result ? result.number : "?"}
          </div>
          <div className="text-center min-h-[4rem]" data-testid="lottery-tv-winner">
            {result?.winner && <div className="font-display font-black uppercase text-3xl sm:text-6xl text-white">{result.winner.name}</div>}
            {result && !result.winner && <div className="font-display font-black uppercase text-2xl sm:text-4xl text-amber-300">No player on #{result.number} — roll again</div>}
            {result?.prize && <div className="text-pb-cyan uppercase tracking-[0.3em] text-sm sm:text-lg mt-2">{result.prize}</div>}
          </div>
          <button data-testid="lottery-tv-generate" onClick={draw} disabled={busy}
            className="bg-pb-red text-black font-black uppercase tracking-[0.3em] text-base sm:text-2xl px-10 py-5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50 flex items-center gap-3">
            <Dices className="w-7 h-7" /> {busy ? "Drawing…" : "Generate"}
          </button>
        </div>
        <div className="max-h-[28vh] overflow-y-auto"><LotteryBoard board={data?.board} highlight={result?.number} testid="lottery-tv-board" /></div>
      </div>
    );
    return inner;
  }

  return (
    <div className="bg-pb-surface border border-pb-border p-5 mt-6" data-testid="td-lottery-panel" ref={boxRef}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold"><Ticket className="w-3.5 h-3.5" /> PBR Lottery</div>
          <h3 className="font-display font-black uppercase text-lg">Number draw · ranked by PBR points at this event</h3>
          <p className="text-xs text-zinc-500">#1 = most PBR points earned here. Set a range, hit Generate — the number drawn is the winner's rank. Numbers above {ranked || 0} have no player (roll again).</p>
        </div>
        <button data-testid="lottery-tv-btn" onClick={() => setShowTv(true)} className="flex items-center gap-1.5 border border-pb-cyan text-pb-cyan text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:bg-pb-cyan hover:text-black transition-colors"><Tv className="w-3.5 h-3.5" /> TV mode</button>
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Draw from 1 to</label>
          <input data-testid="lottery-range" type="number" min={1} value={rangeMax} onChange={(e) => setRangeMax(e.target.value)} placeholder={String(ranked || 1)}
            className="w-28 bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-pb-red" />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Prize (optional)</label>
          <input data-testid="lottery-prize" value={prize} onChange={(e) => setPrize(e.target.value)} placeholder="Free entry · Paddle · Gift card" maxLength={80}
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
        </div>
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-3"><input type="checkbox" checked={excl} onChange={(e) => setExcl(e.target.checked)} className="accent-[#A3E635]" data-testid="lottery-exclude" /> Skip past winners</label>
        <button data-testid="lottery-generate" onClick={draw} disabled={busy}
          className="bg-pb-red text-black font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50 flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Dices className="w-4 h-4" />} Generate
        </button>
      </div>
      <div className="grid md:grid-cols-[220px_1fr] gap-4">
        <div className="border border-pb-border bg-black p-4 text-center" data-testid="lottery-result">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Drawn number</div>
          <div className={`font-display font-black text-7xl tabular-nums leading-none my-2 ${result?.winner ? "text-pb-red" : "text-white"}`} data-testid="lottery-number">
            {spin ? <Spinner target={spin.number} max={max} onDone={landed} /> : result ? result.number : "—"}
          </div>
          <div className="min-h-[2.5rem] text-sm" data-testid="lottery-winner">
            {result?.winner && <span className="font-black text-white">{result.winner.name}</span>}
            {result && !result.winner && <span className="text-amber-300 text-xs font-bold uppercase tracking-widest">No player — roll again</span>}
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto"><LotteryBoard board={data?.board} highlight={result?.number} compact testid="lottery-board" /></div>
      </div>
      {data?.draws?.length > 0 && (
        <div className="mt-4" data-testid="lottery-history">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1.5">Draw history</div>
          <div className="space-y-1">
            {data.draws.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-3 text-xs border border-pb-border/60 px-3 py-1.5" data-testid={`lottery-draw-${d.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-display font-black text-lg text-pb-cyan w-8">{d.number}</span>
                  <span className={`font-bold truncate ${d.winner ? "text-white" : "text-amber-300"}`}>{d.winner ? d.winner.name : "No player on that number"}</span>
                  {d.prize && <span className="text-zinc-500 truncate">· {d.prize}</span>}
                  <span className="text-zinc-600 hidden sm:inline">· 1–{d.range_max} · {new Date(d.drawn_at).toLocaleTimeString()}</span>
                </div>
                <button onClick={() => remove(d.id)} className="text-zinc-600 hover:text-red-400" data-testid={`lottery-draw-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
